import { useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { Input } from "@/components/ui/Input/Input";
import { Modal } from "@/components/ui/Modal/Modal";
import { AppShell } from "@/components/app/AppShell";
import { useAuth } from "@/lib/AuthContext";
import {
  useMembers,
  useWorkspace,
  useUpdateMember,
  useRemoveMember,
  useInvites,
  useCreateInvite,
  useRevokeInvite,
  useRenewInvite,
  useBrands,
  type BrandSummary,
  type Membership,
  type Invite,
} from "@/lib/queries";
import { MEMBER_ROLES } from "@notamen/core/types";
import { ROLE_PERMS, type Permission } from "@notamen/core/workspace";
import { PLANS, PLAN_IDS } from "@notamen/core/billing";
import styles from "./Members.module.css";

/**
 * Members — workspace membership management surface.
 *
 * Lists every workspace membership and (for owner/admin callers) lets
 * them change roles, remove people, and mint copy-link invites. The
 * controls mirror the server-side invariants enforced by `can(role,
 * "manageMembers")` + the owner / last-owner guards so callers don't
 * hit avoidable 400s — but the server is the source of truth and
 * re-checks every mutation.
 *
 * Invites are existing-accounts-only and email-bound: an admin mints a
 * link, shares it, and the recipient (who already has an account with
 * that email) opens it and joins. No email is sent from here.
 *
 * Design source of truth: docs/DESIGN_SPEC.md §4.12.
 */

/** Role accent colors used by the roles legend + member dots/avatars. */
const ROLE_COLOR: Record<Membership["role"], string> = {
  owner: "var(--accent)",
  admin: "var(--accent)",
  approver: "var(--success)",
  editor: "var(--info)",
  viewer: "var(--text-2)",
};

/** Short human labels for the permission keys in ROLE_PERMS. */
const PERMISSION_LABELS: Record<Permission, string> = {
  manageWorkspace: "Manage workspace",
  manageBrands: "Manage brands",
  manageMembers: "Manage members",
  manageSso: "Manage SSO",
  generate: "Generate drafts",
  generateMedia: "Generate media",
  editDraft: "Edit drafts",
  approve: "Approve",
  publish: "Publish",
  comment: "Comment",
  view: "View",
  viewTrends: "View trends",
  manageTrendSources: "Manage trend sources",
};

/** Render order for the roles legend (highest privilege first). */
const ROLE_ORDER: Membership["role"][] = [
  "owner",
  "admin",
  "editor",
  "approver",
  "viewer",
];

/** Pull a human-readable message off a thrown mutation error. */
function errMessage(e: unknown): string {
  if (e && typeof e === "object" && "message" in e) {
    const msg = (e as { message?: unknown }).message;
    if (typeof msg === "string" && msg.trim()) return msg;
  }
  return "Something went wrong. Please try again.";
}

/** Display label for the Member column: email if known, else a short id. */
function memberLabel(m: Membership): string {
  if (m.email) return m.email;
  // Shorten an opaque Cognito sub so the cell stays readable.
  return m.userId.length > 12 ? `${m.userId.slice(0, 8)}…${m.userId.slice(-4)}` : m.userId;
}

/** First initial for the member avatar (email if known, else id). */
function memberInitial(m: Membership): string {
  const base = (m.email ?? m.userId).trim();
  return base ? base.charAt(0).toUpperCase() : "?";
}

/** Build the shareable accept link for an invite token. */
function inviteLink(ws: string | undefined, token: string): string {
  return `${window.location.origin}/w/${ws}/join/${token}`;
}

/** Format an ISO timestamp as a short local date (e.g. "May 28, 2026"). */
function formatExpiry(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** Relative "last active" label. Absent → "Never" (no activity recorded yet). */
function relativeActive(iso: string | undefined): string {
  if (!iso) return "Never";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "Never";
  const diff = Date.now() - t;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "Just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `${d}d ago`;
  return formatExpiry(iso);
}

/** Whether a member was active within the last 7 days (real data only). */
function activeWithin7d(m: Membership): boolean {
  if (!m.lastActiveAt) return false;
  const t = Date.parse(m.lastActiveAt);
  return !Number.isNaN(t) && Date.now() - t <= WEEK_MS;
}

/**
 * Honest seats label for the hero — "5 of 5 seats used", or
 * "5 seats · unlimited" when the plan has no cap.
 *
 * `Workspace.plan` is aligned with `PLAN_IDS` and the API read path
 * coerces legacy `"team"` rows to `"studio"` via `normalizePlan`, so
 * the chip should always have a real PLANS entry. The fallback
 * branches stay as belt-and-braces: an old in-flight tab could pull
 * an unmigrated workspace from a cache, and a future write path could
 * skip normalization — we'd rather render "X seats used" than crash
 * the page.
 */
function formatSeats(used: number, plan: string | undefined): string {
  const noun = used === 1 ? "seat" : "seats";
  if (!plan) return `${used} ${noun} used`;
  const entry = (PLANS as Record<string, { seats: number | null }>)[plan];
  if (!entry) return `${used} ${noun} used`;
  if (entry.seats === null) return `${used} ${noun} · unlimited`;
  return `${used} of ${entry.seats} ${noun} used`;
}

/**
 * Compact "used / total" seat value for the KPI tile — "1 / 5", or
 * "1 / ∞" when the plan has no seat cap. Falls back to just the used
 * count when the plan has no PLANS entry (same belt-and-braces as
 * formatSeats).
 */
function seatCount(used: number, plan: string | undefined): string {
  if (!plan) return String(used);
  const entry = (PLANS as Record<string, { seats: number | null }>)[plan];
  if (!entry) return String(used);
  if (entry.seats === null) return `${used} / ∞`;
  return `${used} / ${entry.seats}`;
}

/**
 * The next plan up from the workspace's current plan, or null when the
 * plan is already the top tier (or unknown). Drives the owner/admin
 * "Upgrade to …" button — no button when there's nothing to upgrade to.
 */
function nextPlanUp(plan: string | undefined): { id: string; name: string } | null {
  if (!plan) return null;
  const idx = (PLAN_IDS as readonly string[]).indexOf(plan);
  if (idx < 0 || idx >= PLAN_IDS.length - 1) return null;
  const nextId = PLAN_IDS[idx + 1]!;
  const entry = (PLANS as Record<string, { name: string }>)[nextId];
  return { id: nextId, name: entry?.name ?? nextId };
}

/**
 * Brand-access display for a member's scope. Empty scope = all brands.
 * Shared by the table cell and the CSV export so the two always agree.
 */
function brandScopeNames(scope: string[], brandNames: Map<string, string>): string[] {
  if (scope.length === 0) return ["All brands"];
  return scope.map((id) => brandNames.get(id) ?? id);
}

/**
 * Build a CSV of exactly what the members table shows. The full members
 * list is already loaded client-side (`useMembers`), so the export is a
 * pure client-side transform — no separate endpoint needed.
 */
function membersToCsv(members: Membership[], brandNames: Map<string, string>): string {
  const esc = (v: string) => {
    // Neutralize CSV formula injection: a leading = + - @ (or tab/CR) can be
    // evaluated as a formula when the file is opened in a spreadsheet app.
    const safe = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
    return `"${safe.replace(/"/g, '""')}"`;
  };
  const header = ["Email", "Role", "Brand access", "Status", "Invited", "Accepted", "Last active"];
  const rows = members.map((m) => [
    m.email ?? m.userId,
    m.role,
    brandScopeNames(m.brandScope ?? [], brandNames).join("; "),
    m.acceptedAt ? "Active" : "Invited",
    m.invitedAt,
    m.acceptedAt ?? "",
    m.lastActiveAt ?? "",
  ]);
  return [header, ...rows]
    .map((r) => r.map((c) => esc(String(c))).join(","))
    .join("\r\n");
}

/** Trigger a client-side file download for a generated CSV string. */
function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function Members() {
  const { ws } = useParams<{ ws: string }>();
  const auth = useAuth();

  if (auth.status === "anonymous") {
    return <Navigate to={`/signin?next=/w/${ws}/members`} replace />;
  }

  return (
    <AppShell currentWorkspaceId={ws}>
      <MembersPage ws={ws} currentUserSub={auth.user?.sub} />
    </AppShell>
  );
}

function MembersPage({
  ws,
  currentUserSub,
}: {
  ws: string | undefined;
  currentUserSub: string | undefined;
}) {
  const workspaceQ = useWorkspace(ws);
  const membersQ = useMembers(ws);
  const invitesQ = useInvites(ws);

  const callerRole = workspaceQ.data?.role;
  const isAdminCaller = callerRole === "owner" || callerRole === "admin";

  const [inviteOpen, setInviteOpen] = useState(false);

  const members = membersQ.data?.members ?? [];
  // Invites only resolve for admins (the query is admin-gated by the API);
  // the count is shown only to admins for the same reason.
  const invites = invitesQ.data?.invites ?? [];

  const workspaceName = workspaceQ.data?.workspace.name;
  const workspacePlan = workspaceQ.data?.workspace.plan;
  // The tier above the current plan, if any — drives the owner/admin
  // "Upgrade to …" button (no button on the top tier).
  const nextPlan = nextPlanUp(workspacePlan);

  return (
    <div className={styles.page}>
      <Btn
        href={`/w/${ws}`}
        variant="ghost"
        size="sm"
        iconLeft={<Icon name="arrow-left" size={13} />}
      >
        Back to workspace
      </Btn>

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <section className={styles.hero}>
        <div className={styles.heroBody}>
          <span className={styles.heroChips}>
            <span className={styles.chip}>
              <Icon name="user" size={12} /> Workspace
            </span>
            <span className={styles.chip}>
              <Icon name="user" size={12} /> {formatSeats(members.length, workspacePlan)}
            </span>
          </span>
          <h1 className={styles.heroTitle}>
            <span className={styles.heroTitleMuted}>Manage who can do what across</span>{" "}
            <b className={styles.heroTitleAccent}>{workspaceName ?? "this workspace"}</b>
            <span className={styles.heroTitleMuted}>.</span>
          </h1>
          <p className={styles.heroLede}>
            Roles map to capabilities that are enforced server-side, never just
            hidden in the UI — an editor can never approve or publish, an
            approver can never edit brand settings. Invites are copy-link only:
            you mint a link bound to an existing account’s email and share it
            yourself. No email is sent from here.
          </p>
        </div>
        {/* Invite + upgrade are owner/admin only. Non-admins get no affordance.
            The upgrade button only shows when there's a higher tier to move
            to (hidden on the top plan). */}
        {isAdminCaller && (
          <div className={styles.heroActions}>
            {nextPlan && (
              <Btn
                href={`/w/${ws}/billing`}
                variant="secondary"
                iconLeft={<Icon name="arrow-up-right" size={14} />}
              >
                Upgrade to {nextPlan.name}
              </Btn>
            )}
            <Btn
              variant="primary"
              iconLeft={<Icon name="send" size={14} />}
              onClick={() => setInviteOpen(true)}
            >
              Invite member
            </Btn>
          </div>
        )}
      </section>

      {/* ── KPI tiles ────────────────────────────────────────────── */}
      <div className={styles.kpiGrid}>
        <KpiTile
          icon="user"
          label="Seats used"
          value={membersQ.isLoading ? "—" : seatCount(members.length, workspacePlan)}
          sub="members / seats on your plan"
        />
        <KpiTile
          icon="check-circle"
          label="Active this week"
          value={membersQ.isLoading ? "—" : String(members.filter(activeWithin7d).length)}
          sub="active in the last 7 days"
        />
        <KpiTile
          icon="lock"
          label="With 2FA"
          value={
            membersQ.isLoading
              ? "—"
              : `${members.filter((m) => m.twoFactorEnabled === true).length} / ${members.length}`
          }
          sub="software-token MFA"
        />
        {isAdminCaller && (
          <KpiTile
            icon="send"
            label="Pending invites"
            value={invitesQ.isLoading ? "—" : String(invites.length)}
            sub="copy-link, awaiting accept"
            tone="accent"
          />
        )}
      </div>

      {/* ── Roles legend (derived from the real permission matrix) ── */}
      <RolesLegend members={members} />

      {/* ── People (search + role filter + table) ────────────────── */}
      <MembersTable ws={ws} currentUserSub={currentUserSub} />

      {isAdminCaller && <PendingInvites ws={ws} />}

      <AccountPolicy />

      {isAdminCaller && (
        <InviteModal
          ws={ws}
          callerRole={callerRole}
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
        />
      )}
    </div>
  );
}

/** Compact stat tile (uppercase label + leading icon, large value, sub). */
function KpiTile({
  icon,
  label,
  value,
  sub,
  tone = "default",
}: {
  icon: "user" | "send" | "check-circle" | "lock";
  label: string;
  value: string;
  sub: string;
  tone?: "default" | "accent";
}) {
  return (
    <article className={`${styles.kpi} ${tone === "accent" ? styles.kpiAccent : ""}`}>
      <span className={styles.kpiLabel}>
        <Icon name={icon} size={12} /> {label}
      </span>
      <span className={styles.kpiValue}>{value}</span>
      <span className={styles.kpiSub}>{sub}</span>
    </article>
  );
}

/**
 * Roles legend. Every card is derived from the real ROLE_PERMS matrix —
 * the capability list is generated from the TRUE permission flags so it
 * can never drift from what the server actually enforces.
 */
function RolesLegend({ members }: { members: Membership[] }) {
  const countFor = (role: Membership["role"]) =>
    members.filter((m) => m.role === role).length;

  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <h2 className={styles.sectionTitle}>Roles</h2>
        <p className={styles.sectionSub}>
          What each role can do in this workspace, enforced server-side.
        </p>
      </div>
      <div className={styles.rolesGrid}>
        {ROLE_ORDER.map((role) => {
          const perms = ROLE_PERMS[role];
          const granted = (Object.keys(perms) as Permission[]).filter(
            (p) => perms[p],
          );
          return (
            <article
              key={role}
              className={styles.roleCard}
              style={{ borderLeftColor: ROLE_COLOR[role] }}
            >
              <div className={styles.roleCardHead}>
                <span
                  className={styles.roleCardName}
                  style={{ color: ROLE_COLOR[role] }}
                >
                  {role}
                </span>
                <span className={styles.roleCardCount}>{countFor(role)}</span>
              </div>
              <ul className={styles.roleCaps}>
                {granted.map((p) => (
                  <li key={p} className={styles.roleCap}>
                    <Icon
                      name="check"
                      size={11}
                      strokeWidth={3}
                      color={ROLE_COLOR[role]}
                    />
                    <span>{PERMISSION_LABELS[p]}</span>
                  </li>
                ))}
              </ul>
            </article>
          );
        })}
      </div>
    </section>
  );
}

/**
 * "Account security & policy" panel — two honest informational cards.
 * No fake "Adjust" / "Manage policy" affordances; the cards describe the
 * actual state of the system today (opt-in 2FA, 24h Cognito-imposed
 * session cap). Visible to everyone; the cards link out where there's a
 * real action (e.g. /account#2fa for personal enrolment).
 */
function AccountPolicy() {
  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <h2 className={styles.sectionTitle}>Account security &amp; policy</h2>
        <p className={styles.sectionSub}>
          What's set today across this workspace's auth surface.
        </p>
      </div>
      <div className={styles.policyGrid}>
        <article className={styles.policyCard}>
          <span className={styles.policyIcon} aria-hidden="true">
            <Icon name="lock" size={18} />
          </span>
          <div>
            <h3 className={styles.policyTitle}>Two-factor authentication</h3>
            <p className={styles.policyBody}>
              Opt-in for every member — anyone can enrol from{" "}
              <Link to="/account#2fa">their account page</Link>. There's
              no required-for-role policy today; the per-workspace toggle
              for that will default OFF when it lands.
            </p>
          </div>
        </article>
        <article className={styles.policyCard}>
          <span className={styles.policyIcon} aria-hidden="true">
            <Icon name="info" size={18} />
          </span>
          <div>
            <h3 className={styles.policyTitle}>Session lifetime</h3>
            <p className={styles.policyBody}>
              24 hours, capped by AWS Cognito — that limit can't be
              raised. Sessions stay active as long as you keep using the
              app.
            </p>
          </div>
        </article>
      </div>
    </section>
  );
}

/**
 * Stateless brand-scope picker — a radio toggle between "All brands" and
 * "Specific brands" with a checkbox list when the latter is selected. Both
 * the row scope-edit modal and the invite modal use it; keeping the mode
 * and checked state in the parent means switching between admin/non-admin
 * roles in the invite form preserves the user's checkbox work instead of
 * resetting on each toggle.
 *
 * NOTE: `brands` comes from `useBrands(ws)`, which is filtered to the
 * caller's own scope as of PR5a. This picker is only mounted by admin-
 * gated callers (the row "Edit access" button and the invite modal — both
 * owner/admin only), so the filter is a no-op for them. If this ever gets
 * exposed to non-admins, the brand list would silently shrink to the
 * caller's own scope — keep the affordance gated.
 */
function BrandScopePicker({
  brands,
  mode,
  checked,
  onModeChange,
  onCheckedChange,
}: {
  brands: BrandSummary[];
  mode: "all" | "specific";
  checked: string[];
  onModeChange: (next: "all" | "specific") => void;
  onCheckedChange: (next: string[]) => void;
}) {
  function toggle(id: string) {
    onCheckedChange(
      checked.includes(id) ? checked.filter((x) => x !== id) : [...checked, id],
    );
  }
  return (
    <div className={styles.scopePicker}>
      <label className={styles.scopeRadio}>
        <input
          type="radio"
          name="brand-scope-mode"
          checked={mode === "all"}
          onChange={() => onModeChange("all")}
        />
        <span className={styles.scopeRadioLabel}>All brands</span>
        <span className={styles.scopeRadioHint}>
          Access to every brand in this workspace.
        </span>
      </label>
      <label className={styles.scopeRadio}>
        <input
          type="radio"
          name="brand-scope-mode"
          checked={mode === "specific"}
          onChange={() => onModeChange("specific")}
        />
        <span className={styles.scopeRadioLabel}>Specific brands</span>
        <span className={styles.scopeRadioHint}>
          Access only to the brands you choose below.
        </span>
      </label>
      {mode === "specific" && (
        <>
          <ul className={styles.scopeList}>
            {brands.length === 0 ? (
              <li className={styles.scopeEmpty}>
                No brands in this workspace yet.
              </li>
            ) : (
              brands.map((b) => (
                <li key={b.id}>
                  <label className={styles.scopeCheck}>
                    <input
                      type="checkbox"
                      checked={checked.includes(b.id)}
                      onChange={() => toggle(b.id)}
                    />
                    <span>{b.name}</span>
                  </label>
                </li>
              ))
            )}
          </ul>
          {brands.length > 0 && checked.length === 0 && (
            <p className={styles.scopeHint}>
              Select at least one brand, or choose "All brands".
            </p>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Per-row "Edit brand access" modal. Owner/admin caller only; never opens
 * for owner/admin target rows (they're exempt — the server rejects
 * brandScope writes on those rows with a 400 to match the UI gate).
 */
function EditBrandScopeModal({
  member,
  brands,
  updateMember,
  onClose,
}: {
  member: Membership;
  brands: BrandSummary[];
  updateMember: UpdateMemberMutation;
  onClose: () => void;
}) {
  const initialScope = member.brandScope ?? [];
  const [mode, setMode] = useState<"all" | "specific">(
    initialScope.length === 0 ? "all" : "specific",
  );
  const [checked, setChecked] = useState<string[]>(initialScope);

  const valid = mode === "all" || checked.length > 0;
  const saving =
    updateMember.isPending && updateMember.variables?.userId === member.userId;
  const saveError =
    updateMember.isError && updateMember.variables?.userId === member.userId
      ? errMessage(updateMember.error)
      : null;

  function save() {
    if (!valid) return;
    updateMember.mutate(
      { userId: member.userId, brandScope: mode === "all" ? [] : checked },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <Modal
      open={true}
      title={`Brand access — ${memberLabel(member)}`}
      onClose={onClose}
      footer={
        <>
          <Btn variant="ghost" onClick={onClose} type="button">
            Cancel
          </Btn>
          <Btn
            variant="primary"
            onClick={save}
            disabled={!valid || saving}
            loading={saving}
          >
            Save
          </Btn>
        </>
      }
    >
      <div className={styles.scopeForm}>
        <p className={styles.muted}>
          Enforced server-side — this member will only access the brands you
          allow. Owner and admin members are exempt and always have access to
          every brand.
        </p>
        <BrandScopePicker
          brands={brands}
          mode={mode}
          checked={checked}
          onModeChange={setMode}
          onCheckedChange={setChecked}
        />
        {saveError && (
          <p className={styles.fieldError}>
            <Icon name="alert" size={13} /> {saveError}
          </p>
        )}
      </div>
    </Modal>
  );
}

/**
 * Invite form modal. Mints a copy-link invite bound to the entered
 * email. On success, swaps the form for the generated link + Copy
 * button rather than closing — the admin needs to grab the link.
 */
function InviteModal({
  ws,
  callerRole,
  open,
  onClose,
}: {
  ws: string | undefined;
  callerRole: Membership["role"] | undefined;
  open: boolean;
  onClose: () => void;
}) {
  const createInvite = useCreateInvite(ws);
  const brandsQ = useBrands(ws);
  const brands = brandsQ.data?.brands ?? [];

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Invite["role"]>("viewer");
  const [emailTouched, setEmailTouched] = useState(false);
  const [copied, setCopied] = useState(false);
  // Scope state lives here (not inside BrandScopePicker) so toggling the role
  // between admin/non-admin preserves the user's checkbox work.
  const [scopeMode, setScopeMode] = useState<"all" | "specific">("all");
  const [scopeChecked, setScopeChecked] = useState<string[]>([]);

  // Only owners can invite owners — the server enforces this too.
  const roleOptions = callerRole === "owner"
    ? MEMBER_ROLES
    : MEMBER_ROLES.filter((r) => r !== "owner");

  // Owner and admin members are exempt from per-brand scoping (server rejects
  // brandScope on those roles). Hide the picker and don't send brandScope
  // when the selected role is owner/admin.
  const isAdminRole = role === "owner" || role === "admin";
  const scopeValid =
    isAdminRole || scopeMode === "all" || scopeChecked.length > 0;

  const created = createInvite.data?.invite;
  const link = created ? inviteLink(ws, created.token) : "";

  function close() {
    // Reset for the next invite so a re-open starts clean.
    createInvite.reset();
    setEmail("");
    setRole("viewer");
    setEmailTouched(false);
    setCopied(false);
    setScopeMode("all");
    setScopeChecked([]);
    onClose();
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setEmailTouched(true);
    if (!email.trim()) return;
    if (!scopeValid) return;
    const vars: { email: string; role: Invite["role"]; brandScope?: string[] } = {
      email: email.trim(),
      role,
    };
    if (!isAdminRole) {
      vars.brandScope = scopeMode === "all" ? [] : scopeChecked;
    }
    createInvite.mutate(vars);
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be unavailable (insecure context / denied perms).
      // The link stays visible in the read-only field for manual copy.
      setCopied(false);
    }
  }

  const emailInvalid = emailTouched && !email.trim();

  return (
    <Modal
      open={open}
      title={created ? "Invite link ready" : "Invite a member"}
      onClose={close}
      footer={
        created ? (
          <Btn variant="primary" onClick={close}>
            Done
          </Btn>
        ) : (
          <>
            <Btn variant="ghost" onClick={close} type="button">
              Cancel
            </Btn>
            <Btn
              variant="primary"
              type="submit"
              form="invite-form"
              loading={createInvite.isPending}
              disabled={!scopeValid}
              iconLeft={<Icon name="send" size={14} />}
            >
              Create invite link
            </Btn>
          </>
        )
      }
    >
      {created ? (
        <div className={styles.inviteResult}>
          <p className={styles.inviteResultLede}>
            Share this link with <strong>{created.email}</strong>. They sign in
            with that email and join automatically.
          </p>
          <div className={styles.linkRow}>
            <Input
              icon="link"
              readOnly
              value={link}
              aria-label="Invite link"
              onFocus={(e) => e.currentTarget.select()}
            />
            <Btn
              variant="secondary"
              onClick={() => void copyLink()}
              iconLeft={<Icon name={copied ? "check" : "copy"} size={14} />}
            >
              {copied ? "Copied!" : "Copy"}
            </Btn>
          </div>
          <p className={styles.muted}>
            This link only works for {created.email} and expires{" "}
            {formatExpiry(created.expiresAt)} (7 days).
          </p>
        </div>
      ) : (
        <form id="invite-form" onSubmit={onSubmit} noValidate className={styles.inviteForm}>
          <label className={styles.fieldLabel} htmlFor="invite-email">
            Email
          </label>
          <Input
            id="invite-email"
            icon="mail"
            type="email"
            inputMode="email"
            autoComplete="off"
            autoFocus
            value={email}
            invalid={emailInvalid}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setEmailTouched(true)}
            placeholder="teammate@brand.com"
          />
          {emailInvalid && (
            <p className={styles.fieldError}>
              <Icon name="alert" size={12} /> Enter an email address.
            </p>
          )}

          <label className={styles.fieldLabel} htmlFor="invite-role">
            Role
          </label>
          <select
            id="invite-role"
            className={styles.roleSelect}
            value={role}
            onChange={(e) => setRole(e.target.value as Invite["role"])}
          >
            {roleOptions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>

          {!isAdminRole && (
            <>
              <span className={styles.fieldLabel}>Brand access</span>
              <BrandScopePicker
                brands={brands}
                mode={scopeMode}
                checked={scopeChecked}
                onModeChange={setScopeMode}
                onCheckedChange={setScopeChecked}
              />
            </>
          )}

          <p className={styles.muted}>
            The recipient must already have an account with this email. You’ll
            get a shareable link to send them — no email is sent from here.
          </p>

          {createInvite.isError && (
            <p className={styles.fieldError}>
              <Icon name="alert" size={13} /> {errMessage(createInvite.error)}
            </p>
          )}
        </form>
      )}
    </Modal>
  );
}

/** Pending (un-accepted) invites with copy-link + revoke controls. */
function PendingInvites({ ws }: { ws: string | undefined }) {
  const invitesQ = useInvites(ws);
  const revokeInvite = useRevokeInvite(ws);
  const renewInvite = useRenewInvite(ws);

  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  async function copyFor(token: string) {
    try {
      await navigator.clipboard.writeText(inviteLink(ws, token));
      setCopiedToken(token);
      window.setTimeout(
        () => setCopiedToken((t) => (t === token ? null : t)),
        2000,
      );
    } catch {
      setCopiedToken(null);
    }
  }

  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <h2 className={styles.sectionTitle}>Pending invites</h2>
        <p className={styles.sectionSub}>
          Copy-link invites awaiting acceptance. Each is bound to its email.
        </p>
      </div>

      <PendingInvitesBody
        invitesQ={invitesQ}
        revokeInvite={revokeInvite}
        renewInvite={renewInvite}
        copiedToken={copiedToken}
        onCopy={copyFor}
      />
    </section>
  );
}

type InvitesQuery = ReturnType<typeof useInvites>;
type RevokeInviteMutation = ReturnType<typeof useRevokeInvite>;
type RenewInviteMutation = ReturnType<typeof useRenewInvite>;

function PendingInvitesBody({
  invitesQ,
  revokeInvite,
  renewInvite,
  copiedToken,
  onCopy,
}: {
  invitesQ: InvitesQuery;
  revokeInvite: RevokeInviteMutation;
  renewInvite: RenewInviteMutation;
  copiedToken: string | null;
  onCopy: (token: string) => void;
}) {
  if (invitesQ.isLoading) {
    return (
      <div className={styles.emptyPane}>
        <Icon name="refresh" size={22} />
        <p>Loading invites…</p>
      </div>
    );
  }

  if (invitesQ.isError) {
    return (
      <div className={styles.emptyPane}>
        <Icon name="alert" size={22} />
        <p>Couldn’t load invites.</p>
        <p className={styles.emptyHint}>{errMessage(invitesQ.error)}</p>
        <Btn variant="secondary" size="sm" onClick={() => void invitesQ.refetch()}>
          Retry
        </Btn>
      </div>
    );
  }

  const invites = invitesQ.data?.invites ?? [];

  if (invites.length === 0) {
    return (
      <div className={styles.emptyPane}>
        <Icon name="send" size={22} />
        <p>No pending invites.</p>
        <p className={styles.emptyHint}>
          Use “Invite member” above to mint a shareable copy-link.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.inviteList}>
      {invites.map((inv) => (
        <InviteRow
          key={inv.token}
          invite={inv}
          revokeInvite={revokeInvite}
          renewInvite={renewInvite}
          copied={copiedToken === inv.token}
          onCopy={onCopy}
        />
      ))}
    </div>
  );
}

function InviteRow({
  invite: inv,
  revokeInvite,
  renewInvite,
  copied,
  onCopy,
}: {
  invite: Invite;
  revokeInvite: RevokeInviteMutation;
  renewInvite: RenewInviteMutation;
  copied: boolean;
  onCopy: (token: string) => void;
}) {
  const revoking =
    revokeInvite.isPending && revokeInvite.variables?.token === inv.token;
  const revokeError =
    revokeInvite.isError && revokeInvite.variables?.token === inv.token
      ? errMessage(revokeInvite.error)
      : null;
  const renewing =
    renewInvite.isPending && renewInvite.variables?.token === inv.token;
  const renewError =
    renewInvite.isError && renewInvite.variables?.token === inv.token
      ? errMessage(renewInvite.error)
      : null;

  return (
    <article className={styles.inviteCard}>
      <div className={styles.inviteIcon}>
        <Icon name="send" size={14} />
      </div>
      <div className={styles.inviteMeta}>
        <div className={styles.inviteTopRow}>
          <span className={styles.inviteEmail}>{inv.email}</span>
          <span
            className={`${styles.roleChip} ${styles.roleChipAccent}`}
            data-role={inv.role}
          >
            {inv.role}
          </span>
        </div>
        <span className={styles.inviteExpiry}>
          Expires {formatExpiry(inv.expiresAt)}
        </span>
        {(revokeError || renewError) && (
          <span className={styles.inviteError}>
            <Icon name="alert" size={12} /> {revokeError ?? renewError}
          </span>
        )}
      </div>
      <div className={styles.inviteActions}>
        <Btn
          variant="ghost"
          size="sm"
          onClick={() => onCopy(inv.token)}
          iconLeft={<Icon name={copied ? "check" : "copy"} size={13} />}
        >
          {copied ? "Copied!" : "Copy link"}
        </Btn>
        <Btn
          variant="ghost"
          size="sm"
          loading={renewing}
          iconLeft={<Icon name="refresh" size={13} />}
          onClick={() => renewInvite.mutate({ token: inv.token })}
        >
          Renew
        </Btn>
        <Btn
          variant="ghost"
          size="sm"
          loading={revoking}
          iconLeft={<Icon name="x" size={13} />}
          onClick={() => {
            if (window.confirm(`Revoke the invite for ${inv.email}?`)) {
              revokeInvite.mutate({ token: inv.token });
            }
          }}
        >
          Revoke
        </Btn>
      </div>
    </article>
  );
}

function MembersTable({
  ws,
  currentUserSub,
}: {
  ws: string | undefined;
  currentUserSub: string | undefined;
}) {
  const membersQ = useMembers(ws);
  const workspaceQ = useWorkspace(ws);
  const brandsQ = useBrands(ws);
  const updateMember = useUpdateMember(ws);
  const removeMember = useRemoveMember(ws);

  const callerRole = workspaceQ.data?.role;
  const isAdminCaller = callerRole === "owner" || callerRole === "admin";

  // Map brand id → display name for the Brand access column. Falls back to
  // the raw id when the brands list hasn't resolved or the id is unknown.
  const brandNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of brandsQ.data?.brands ?? []) map.set(b.id, b.name);
    return map;
  }, [brandsQ.data]);

  // Client-side filters.
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<"all" | Membership["role"]>("all");

  // Per-row brand-scope editor — null when no row is being edited.
  const [editingMember, setEditingMember] = useState<Membership | null>(null);
  const brandsList = brandsQ.data?.brands ?? [];

  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <h2 className={styles.sectionTitle}>People</h2>
        <p className={styles.sectionSub}>
          Everyone with access to this workspace.{" "}
          {isAdminCaller
            ? "As an owner or admin you can change roles and remove people."
            : "Owners and admins can change roles and remove people."}
        </p>
        <div className={styles.peopleControls}>
          <div className={styles.searchBox}>
            <Input
              icon="user"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email or role…"
              aria-label="Search members"
            />
          </div>
          <div className={styles.segmented} role="group" aria-label="Filter by role">
            <button
              type="button"
              className={`${styles.segment} ${roleFilter === "all" ? styles.segmentActive : ""}`}
              onClick={() => setRoleFilter("all")}
            >
              All
            </button>
            {ROLE_ORDER.map((r) => (
              <button
                key={r}
                type="button"
                className={`${styles.segment} ${roleFilter === r ? styles.segmentActive : ""}`}
                onClick={() => setRoleFilter(r)}
              >
                {r}
              </button>
            ))}
          </div>
          {isAdminCaller && (
            <Btn
              variant="secondary"
              size="sm"
              disabled={(membersQ.data?.members ?? []).length === 0}
              onClick={() =>
                downloadCsv(
                  `members-${ws ?? "workspace"}.csv`,
                  membersToCsv(membersQ.data?.members ?? [], brandNames),
                )
              }
            >
              Export CSV
            </Btn>
          )}
        </div>
      </div>

      <MembersBody
        membersQ={membersQ}
        workspaceQ={workspaceQ}
        callerRole={callerRole}
        isAdminCaller={isAdminCaller}
        currentUserSub={currentUserSub}
        updateMember={updateMember}
        removeMember={removeMember}
        brandNames={brandNames}
        search={search}
        roleFilter={roleFilter}
        onEditBrandScope={setEditingMember}
      />

      {editingMember && (
        <EditBrandScopeModal
          key={editingMember.userId}
          member={editingMember}
          brands={brandsList}
          updateMember={updateMember}
          onClose={() => setEditingMember(null)}
        />
      )}
    </section>
  );
}

type MembersQuery = ReturnType<typeof useMembers>;
type WorkspaceQuery = ReturnType<typeof useWorkspace>;
type UpdateMemberMutation = ReturnType<typeof useUpdateMember>;
type RemoveMemberMutation = ReturnType<typeof useRemoveMember>;

function MembersBody({
  membersQ,
  workspaceQ,
  callerRole,
  isAdminCaller,
  currentUserSub,
  updateMember,
  removeMember,
  brandNames,
  search,
  roleFilter,
  onEditBrandScope,
}: {
  membersQ: MembersQuery;
  workspaceQ: WorkspaceQuery;
  callerRole: Membership["role"] | undefined;
  isAdminCaller: boolean;
  currentUserSub: string | undefined;
  updateMember: UpdateMemberMutation;
  removeMember: RemoveMemberMutation;
  brandNames: Map<string, string>;
  search: string;
  roleFilter: "all" | Membership["role"];
  onEditBrandScope: (m: Membership) => void;
}) {
  // Wait for both the members list and the caller's role before deciding
  // which controls to render — gating on a missing role would briefly show
  // read-only to an admin.
  if (membersQ.isLoading || workspaceQ.isLoading) {
    return (
      <div className={styles.emptyPane}>
        <Icon name="refresh" size={22} />
        <p>Loading members…</p>
      </div>
    );
  }

  if (membersQ.isError) {
    return (
      <div className={styles.emptyPane}>
        <Icon name="alert" size={22} />
        <p>Couldn’t load members.</p>
        <p className={styles.emptyHint}>{errMessage(membersQ.error)}</p>
        <Btn variant="secondary" size="sm" onClick={() => void membersQ.refetch()}>
          Retry
        </Btn>
      </div>
    );
  }

  const members = membersQ.data?.members ?? [];

  if (members.length === 0) {
    return (
      <div className={styles.emptyPane}>
        <Icon name="user" size={22} />
        <p>No members yet.</p>
        <p className={styles.emptyHint}>
          Workspace members will list here. Use “Invite member” to add people.
        </p>
      </div>
    );
  }

  const ownerCount = members.filter((m) => m.role === "owner").length;

  // Apply client-side search (email or role substring) + role filter.
  const needle = search.trim().toLowerCase();
  const visible = members.filter((m) => {
    if (roleFilter !== "all" && m.role !== roleFilter) return false;
    if (!needle) return true;
    const haystack = `${m.email ?? ""} ${m.userId} ${m.role}`.toLowerCase();
    return haystack.includes(needle);
  });

  if (visible.length === 0) {
    return (
      <div className={styles.emptyPane}>
        <Icon name="user" size={22} />
        <p>No members match your filters.</p>
        <p className={styles.emptyHint}>
          Clear the search or pick a different role to see everyone.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.tableCard}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th scope="col">Member</th>
            <th scope="col">Role</th>
            <th scope="col">Brand access</th>
            <th scope="col">Status</th>
            <th scope="col">Added</th>
            <th scope="col">Last active</th>
            <th scope="col" className={styles.actionsCol}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {visible.map((m) => (
            <MemberRow
              key={m.userId}
              member={m}
              callerRole={callerRole}
              isAdminCaller={isAdminCaller}
              isSelf={m.userId === currentUserSub}
              ownerCount={ownerCount}
              updateMember={updateMember}
              removeMember={removeMember}
              brandNames={brandNames}
              onEditBrandScope={onEditBrandScope}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MemberRow({
  member: m,
  callerRole,
  isAdminCaller,
  isSelf,
  ownerCount,
  updateMember,
  removeMember,
  brandNames,
  onEditBrandScope,
}: {
  member: Membership;
  callerRole: Membership["role"] | undefined;
  isAdminCaller: boolean;
  isSelf: boolean;
  ownerCount: number;
  updateMember: UpdateMemberMutation;
  removeMember: RemoveMemberMutation;
  brandNames: Map<string, string>;
  onEditBrandScope: (m: Membership) => void;
}) {
  const callerIsOwner = callerRole === "owner";
  const rowIsOwner = m.role === "owner";
  const isLastOwner = rowIsOwner && ownerCount <= 1;

  // Per-row pending state: only block the row whose mutation is in flight.
  const roleSaving =
    updateMember.isPending && updateMember.variables?.userId === m.userId;
  const removing =
    removeMember.isPending && removeMember.variables?.userId === m.userId;

  // Surface the most recent error that targeted THIS row.
  const roleError =
    updateMember.isError && updateMember.variables?.userId === m.userId
      ? errMessage(updateMember.error)
      : null;
  const removeError =
    removeMember.isError && removeMember.variables?.userId === m.userId
      ? errMessage(removeMember.error)
      : null;

  // Only an owner caller may touch an owner row OR promote anyone to owner.
  // Non-admin callers get a fully read-only view.
  const roleEditable =
    isAdminCaller && (callerIsOwner || !rowIsOwner) && !isLastOwner;

  // Role options: hide "owner" from non-owner callers so they can't try to
  // promote (the server would reject it anyway).
  const roleOptions = callerIsOwner
    ? MEMBER_ROLES
    : MEMBER_ROLES.filter((r) => r !== "owner");

  // Remove is allowed for admin callers, but owner rows only by an owner
  // caller, and never the last owner.
  const canRemove = isAdminCaller && (callerIsOwner || !rowIsOwner) && !isLastOwner;

  const lastOwnerTitle = "Promote another owner first.";

  // Brand access is a neutral statement of the member's brand scope — empty
  // scope means all brands. Per-brand enforcement does not exist yet, so this
  // is descriptive, not a restriction.
  const scope = m.brandScope ?? [];

  return (
    <>
      <tr className={styles.row}>
        <td className={styles.cell} data-label="Member">
          <div className={styles.memberCell}>
            <span
              className={styles.avatar}
              style={{ borderColor: ROLE_COLOR[m.role] }}
              aria-hidden="true"
            >
              {memberInitial(m)}
            </span>
            <span className={styles.memberName}>
              {memberLabel(m)}
              {isSelf && <span className={styles.youChip}>you</span>}
              {m.twoFactorEnabled === true ? (
                <span
                  className={styles.tfaBadgeOn}
                  title="Two-factor authentication on"
                  aria-label="2FA on"
                >
                  <Icon name="lock" size={11} />
                </span>
              ) : (
                <span
                  className={styles.tfaBadgeOff}
                  title="Two-factor authentication off"
                  aria-label="2FA off"
                >
                  <Icon name="alert" size={11} />
                </span>
              )}
            </span>
          </div>
        </td>

        <td className={styles.cell} data-label="Role">
          {roleEditable ? (
            <select
              className={styles.roleSelect}
              value={m.role}
              disabled={roleSaving}
              aria-label={`Role for ${memberLabel(m)}`}
              onChange={(e) =>
                updateMember.mutate({
                  userId: m.userId,
                  role: e.target.value as Membership["role"],
                })
              }
            >
              {roleOptions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          ) : (
            <span
              className={styles.roleStatic}
              title={isLastOwner && isAdminCaller ? lastOwnerTitle : undefined}
            >
              <span
                className={styles.roleDot}
                style={{ background: ROLE_COLOR[m.role] }}
                aria-hidden="true"
              />
              <span style={{ color: ROLE_COLOR[m.role] }}>{m.role}</span>
            </span>
          )}
        </td>

        <td className={styles.cell} data-label="Brand access">
          <div className={styles.brandChips}>
            {brandScopeNames(scope, brandNames).map((label) => (
              <span key={label} className={styles.brandChip}>
                {label}
              </span>
            ))}
          </div>
          {isAdminCaller && m.role !== "owner" && m.role !== "admin" && (
            <Btn
              variant="ghost"
              size="sm"
              onClick={() => onEditBrandScope(m)}
              iconLeft={<Icon name="edit" size={12} />}
              aria-label={`Edit brand access for ${memberLabel(m)}`}
            >
              Edit
            </Btn>
          )}
        </td>

        <td className={styles.cell} data-label="Status">
          <span
            className={`${styles.statusPill} ${
              m.acceptedAt ? styles.statusActive : styles.statusInvited
            }`}
          >
            {m.acceptedAt ? "Active" : "Invited"}
          </span>
        </td>

        <td className={styles.cell} data-label="Added">
          <span className={styles.addedDate}>{formatExpiry(m.invitedAt)}</span>
        </td>

        <td className={styles.cell} data-label="Last active">
          <span className={styles.addedDate}>{relativeActive(m.lastActiveAt)}</span>
        </td>

        <td className={`${styles.cell} ${styles.actionsCell}`} data-label="Actions">
          {canRemove ? (
            <Btn
              variant="ghost"
              size="sm"
              loading={removing}
              iconLeft={<Icon name="x" size={13} />}
              onClick={() => {
                if (window.confirm("Remove this member from the workspace?")) {
                  removeMember.mutate({ userId: m.userId });
                }
              }}
            >
              Remove
            </Btn>
          ) : isAdminCaller && isLastOwner ? (
            <Btn variant="ghost" size="sm" disabled title={lastOwnerTitle}>
              Remove
            </Btn>
          ) : (
            <span className={styles.noActions} aria-hidden="true">
              —
            </span>
          )}
        </td>
      </tr>
      {(roleError || removeError) && (
        <tr className={styles.errorRow}>
          <td colSpan={7} className={styles.errorCell}>
            <Icon name="alert" size={13} /> {roleError ?? removeError}
          </td>
        </tr>
      )}
    </>
  );
}
